import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (_e) {
    return "";
  }
}

function DeleteMemberModal({ user, onClose, onConfirm, busy }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-16 px-4"
      onClick={onClose}
      data-testid="users-delete-modal"
    >
      <div
        className="bg-white border border-black w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-black px-6 py-4">
          <div className="mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#fc4707" }}>
            Delete member
          </div>
          <h3 className="serif text-2xl mt-1">This cannot be undone</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-neutral-800">
            <span className="serif text-lg">{user.name || user.email}</span>{" "}
            <span className="mono text-xs text-neutral-500">({user.email})</span> will be permanently removed
            from the members list.
          </p>
          <p className="text-neutral-700 text-sm">
            Any pieces or forum replies they have submitted will remain on the site with the author shown as{" "}
            <span className="italic">Deleted member</span>. If you&rsquo;d rather block access without deleting
            their history, use <span className="italic">Suspend</span> instead.
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            data-testid="users-delete-cancel"
            className="mono text-xs uppercase tracking-[0.2em] px-5 py-2 border border-neutral-300 hover:border-black"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="users-delete-confirm"
            className="bg-red-900 text-white px-5 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-950 disabled:opacity-40"
          >
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPanel() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [pendingId, setPendingId] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const setRole = async (u, role) => {
    setPendingId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      toast.success(role === "admin" ? `${u.email} promoted to editor.` : `${u.email} demoted to member.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingId(null);
    }
  };

  const toggleSuspend = async (u) => {
    setPendingId(u.id);
    try {
      await api.put(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      toast.success(u.suspended ? `${u.email} reinstated.` : `${u.email} suspended.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deletingUser.id}`);
      toast.success(`${deletingUser.email} deleted.`);
      setDeletingUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter(
        (u) =>
          (u.email || "").toLowerCase().includes(q) ||
          (u.name || "").toLowerCase().includes(q)
      )
    : users;

  return (
    <div className="border border-black">
      <div className="border-b border-black p-4 flex items-center justify-between gap-4">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em]" style={{ color: "#fc4707" }}>Members</div>
          <p className="text-sm text-neutral-700 mt-1">
            Promote members to <span className="italic">editors</span>, suspend accounts to block sign-in, or delete permanently.
          </p>
        </div>
        <input
          data-testid="users-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by email or name"
          className="border border-neutral-300 focus:border-black bg-transparent px-3 py-2 focus:outline-none mono text-xs w-64"
        />
      </div>
      {loading ? (
        <div className="p-8 mono text-xs text-neutral-500">Loading&hellip;</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 mono text-xs text-neutral-500">No members found.</div>
      ) : (
        <div className="divide-y divide-neutral-200">
          {filtered.map((u) => {
            const isSelf = current?.id === u.id;
            const isAdmin = u.role === "admin";
            const isSuspended = !!u.suspended;
            const busy = pendingId === u.id;
            return (
              <div
                key={u.id}
                data-testid={`users-row-${u.id}`}
                className={`p-4 flex items-center justify-between gap-4 hover:bg-neutral-50 ${isSuspended ? "opacity-70" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`serif text-lg truncate ${isSuspended ? "line-through text-neutral-500" : ""}`}>
                      {u.name || u.email}
                    </span>
                    {isAdmin && (
                      <span
                        className="mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border"
                        style={{ color: "#fc4707", borderColor: "#fc4707" }}
                      >
                        Editor
                      </span>
                    )}
                    {isSuspended && (
                      <span className="mono text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 border border-red-800 text-red-800">
                        Suspended
                      </span>
                    )}
                    {isSelf && (
                      <span className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">you</span>
                    )}
                  </div>
                  <div className="mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-1 truncate">
                    {u.email}
                    <span className="mx-2 text-neutral-400">/</span>
                    joined {formatDate(u.created_at)}
                  </div>
                </div>
                <div className="shrink-0 flex flex-wrap gap-2 justify-end">
                  {isAdmin ? (
                    <button
                      type="button"
                      data-testid={`users-demote-${u.id}`}
                      onClick={() => setRole(u, "user")}
                      disabled={isSelf || busy}
                      title={isSelf ? "You cannot demote your own account." : "Remove editor privileges"}
                      className="border border-neutral-400 px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:border-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy ? "Saving…" : "Demote"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`users-promote-${u.id}`}
                      onClick={() => setRole(u, "admin")}
                      disabled={busy || isSuspended}
                      title={isSuspended ? "Reinstate the account before promoting" : "Grant editor privileges"}
                      className="bg-black text-white px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {busy ? "Saving…" : "Promote to editor"}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`users-suspend-${u.id}`}
                    onClick={() => toggleSuspend(u)}
                    disabled={isSelf || busy}
                    title={isSelf ? "You cannot suspend your own account." : (isSuspended ? "Restore sign-in access" : "Block sign-in for this member")}
                    className="border border-red-800 text-red-800 px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-800 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {busy ? "Saving…" : (isSuspended ? "Reinstate" : "Suspend")}
                  </button>
                  <button
                    type="button"
                    data-testid={`users-delete-${u.id}`}
                    onClick={() => setDeletingUser(u)}
                    disabled={isSelf || busy}
                    title={isSelf ? "You cannot delete your own account." : "Permanently delete this member"}
                    className="border border-red-900 text-red-900 px-4 py-2 mono text-xs uppercase tracking-[0.2em] hover:bg-red-900 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {deletingUser && (
        <DeleteMemberModal
          user={deletingUser}
          onClose={() => (deleting ? null : setDeletingUser(null))}
          onConfirm={confirmDelete}
          busy={deleting}
        />
      )}
    </div>
  );
}
